/* eslint-disable import/no-unused-modules */

import {
  Capabilities,
  StripePaymentProfile,
} from '../schemas/CapabilitiesSchema';

export interface BookingOption {
  pricingModel: string;
  id: string;
  price: number;
  description: string;
  title: string;
  cancellationPolicy: string;
  subType?: BookingOptionSubType;
  order: number;
  duration?: number;
  locationType: keyof typeof LocationType;
  hours?: string;
  taxCode: string;
  taxRate: string | null;
  interval?: string | number;
  attendeesRequired?: boolean;
  attendeesMaximum?: number | null;
  attendeesMinimum?: number;
  bookingNotes?: string;
  additionalCharges: AdditionalCharges[];
  location?: Location;
  sourceId?: string;
}

export type AdditionalCharges = {
  name: 'Gratuity' | 'Vendor Fees' | 'vendorFees';
  percent: number;
};

export enum BookingOptionSubType {
  addon = 'addon',
  bookingOption = 'bookingOption',
}

export type GetAvailabilitiesPayload = {
  activityId: string;
  bookingOptionId: string;
  startDate: Date;
  endDate: Date;
  tz: string;
};

export type Availabilities = {
  eventDate: string;
  capacity: number;
};

export type Attachment = {
  id: string;
  mimeType: string;
  title: string;
  type: 'menu';
  uploadedAt: string;
  url: string;
};

export type Picture = {
  id: string;
  url: string;
};

export type Media =
  | {
      type: 'image';
      id: string;
      url: string;
    }
  | {
      type: 'video';
      id: string;
      url: string;
      thumbnailUrl: string;
    };

export interface Activity {
  attachments: Attachment[];
  avgRating?: number;
  bachTip?: string;
  bachelorPopularity?: number;
  bachelorettePopularity?: number;
  bachified: boolean;
  bookable: boolean;
  bookingOptions: BookingOption[]; // pricingModel: {person, party}
  cancellationPolicy: string;
  category: string;
  categoryId: string;
  city: string;
  commission: number;
  deadline?: string | number;
  description: string;
  duration?: number;
  faqContent?: string;
  faqLink?: string; // deprecated, use faqContent instead
  featured: boolean;
  hours?: string;
  id: string;
  internalVendorProcessingNotes?: string;
  interval?: string;
  location: Location;
  metadata?: (ExternalSourceMetadata & BACH2SourceMetadata) | undefined;
  pictures: Picture[]; // id, url
  media: Media[];
  popularity: number;
  ratingsCount?: number;
  reviewsCount?: number;
  source?: ActivitySource;
  subCategoryId?: string;
  supplierId?: string;
  tags?: string[];
  title: string;
  type: string;
  vendorDisplayName?: string;
  vendorId: string;
  vendorName: string;
  hasNativeMetafields?: boolean;
  partyTypes?: string[];
  publishedDate?: string;
  partyPro: boolean;
  meta?: Meta;
  suggestedActivityId?: string;
  suggestedActivityText?: string;
  bookingNotes?: string;
  status?: 'published' | 'unpublished';
}

type Meta = {
  capabilities: Capabilities;
};

export type SuggestedActivity = {
  text?: string;
  activity: Activity;
};

export enum LocationType {
  atHome = 'atHome',
  vendorLocation = 'vendorLocation',
}

export type Location = {
  address?: string;
  city?: string;
  country?: string;
  latitude?: number;
  line1?: string;
  line2?: string;
  locationNotes?: string;
  longitude?: number;
  name?: string;
  state?: string;
  offset?: string;
  postalCode?: string;
  tz?: string;
  url?: string;
};

export enum ActivitySource {
  bach = 'bach',
  bach2 = 'bach2',
  externalUrl = 'externalUrl',
  fareharbor = 'fareharbor',
  octo = 'octo',
  peek = 'peek',
  redeam = 'redeam',
  singenuity = 'singenuity',
}

export type ActivitySourceValue = keyof typeof ActivitySource;

export type Tag = {
  name: string;
  type: string;
};

export type ExternalSourceMetadata = {
  externalUrl: string;
  externalUrlCTA?: string;
};

export type BACH2SourceMetadata = {
  stripeId: string;
};

export type Category = {
  id: string;
  name: string;
  subCategories: SubCategory[];
};

export type SubCategory = {
  id: string;
  name: string;
};

export type PaymentProfileOptionValue = Pick<
  StripePaymentProfile,
  | 'platformProcessingFee'
  | 'remittancePeriod'
  | 'remittanceSchedule'
  | 'feeType'
>;

export type PaymentProfileOption = {
  value: PaymentProfileOptionValue;
  label: string;
};
